/**
 * Exhibition search via Storefront API (paginates all exhibition metaobjects).
 */
(function () {
  'use strict';

  var config = window.theme && window.theme.exhibitionSearch;
  if (!config || !config.apiUrl || !config.token) return;

  var hasQuickSearch = !!document.querySelector('[data-quick-search]');
  var hasSearchPage = !!document.querySelector('[data-section-type="search"]');
  if (!hasQuickSearch && !hasSearchPage) return;

  var STOP_WORDS = ['the', 'of', 'and', 'a', 'an', 'in', 'at', 'for', 'to', 'by', 'on', 'or'];
  var MIN_QUERY_LENGTH = config.minQueryLength || 2;
  var CACHE_KEY = 'maddox-exhibitions-cache';
  var CACHE_TTL = config.cacheTtlMs || 300000;
  var TYPE_LABEL = config.typeLabel || config.heading || 'Exhibition';
  var exhibitionsCache = null;
  var exhibitionsLoading = null;

  var QUERY = [
    'query ExhibitionSearch($type: String!, $first: Int!, $after: String) {',
    '  metaobjects(type: $type, first: $first, after: $after) {',
    '    pageInfo { hasNextPage endCursor }',
    '    edges {',
    '      node {',
    '        handle',
    '        onlineStoreUrl',
    '        title: field(key: "title") { value }',
    '        hero_accent: field(key: "hero_accent") { value }',
    '        hero_subheading: field(key: "hero_subheading") { value }',
    '        location: field(key: "location") { value }',
    '        city: field(key: "city") { value }',
    '        country: field(key: "country") { value }',
    '        date_display: field(key: "date_display") { value }',
    '        exhibition_card_summary_text: field(key: "exhibition_card_summary_text") { value }',
    '        about_col_1: field(key: "about_col_1") { value }',
    '        artist_bio: field(key: "artist_bio") { value }',
    '        hero_image: field(key: "hero_image") {',
    '          reference {',
    '            ... on MediaImage {',
    '              image { url(transform: { maxWidth: 600 }) altText }',
    '            }',
    '          }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  function fieldValue(field) {
    if (!field || field.value == null) return '';
    return String(field.value).trim();
  }

  function stripHtml(str) {
    return String(str || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function heroImageFromField(field) {
    if (!field || !field.reference || !field.reference.image) return { url: '', alt: '' };
    return {
      url: field.reference.image.url || '',
      alt: field.reference.image.altText || '',
    };
  }

  function warnMissingUrl(handle) {
    if (window.Shopify && Shopify.designMode) {
      console.warn('[exhibition-search] Missing onlineStoreUrl for exhibition:', handle);
    }
  }

  function normalizeExhibition(node) {
    var hero = heroImageFromField(node.hero_image);
    var url = node.onlineStoreUrl || '';

    if (!url) {
      if (node.handle) {
        warnMissingUrl(node.handle);
        url = '/pages/exhibition/' + node.handle;
      }
    }

    return {
      handle: node.handle || '',
      title: fieldValue(node.title),
      url: url,
      hero_accent: fieldValue(node.hero_accent),
      hero_subheading: fieldValue(node.hero_subheading),
      location: fieldValue(node.location),
      city: fieldValue(node.city),
      country: fieldValue(node.country),
      date_display: fieldValue(node.date_display),
      exhibition_card_summary_text: fieldValue(node.exhibition_card_summary_text),
      about_col_1_text: stripHtml(fieldValue(node.about_col_1)),
      artist_bio_excerpt: stripHtml(fieldValue(node.artist_bio)),
      hero_image_url: hero.url,
      hero_image_alt: hero.alt,
    };
  }

  function readSessionCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.at || Date.now() - parsed.at > CACHE_TTL) return null;
      return parsed.list || null;
    } catch (e) {
      return null;
    }
  }

  function writeSessionCache(list) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), list: list }));
    } catch (e) {
      /* ignore quota errors */
    }
  }

  function apiFetch(variables) {
    return fetch(config.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.token,
      },
      body: JSON.stringify({ query: QUERY, variables: variables }),
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok) throw new Error((json && json.message) || 'HTTP ' + res.status);
        if (json.errors && json.errors.length) {
          throw new Error(json.errors.map(function (e) { return e.message; }).join('; '));
        }
        return json;
      });
    });
  }

  function loadExhibitions() {
    if (exhibitionsCache) return Promise.resolve(exhibitionsCache);
    if (exhibitionsLoading) return exhibitionsLoading;

    var cached = readSessionCache();
    if (cached) {
      exhibitionsCache = cached;
      return Promise.resolve(exhibitionsCache);
    }

    exhibitionsLoading = (function fetchPage(after, acc) {
      return apiFetch({
        type: config.metaobjectType || 'exhibition',
        first: 100,
        after: after || null,
      }).then(function (json) {
        var mo = json.data && json.data.metaobjects;
        var edges = (mo && mo.edges) || [];
        edges.forEach(function (edge) {
          if (edge && edge.node) acc.push(normalizeExhibition(edge.node));
        });

        if (mo && mo.pageInfo && mo.pageInfo.hasNextPage) {
          return fetchPage(mo.pageInfo.endCursor, acc);
        }
        return acc;
      });
    })(null, [])
      .then(function (list) {
        exhibitionsCache = list;
        writeSessionCache(list);
        return list;
      })
      .catch(function () {
        exhibitionsCache = [];
        return exhibitionsCache;
      });

    return exhibitionsLoading;
  }

  function primaryHaystack(ex) {
    var handleNorm = (ex.handle || '').replace(/-/g, ' ');
    return [
      ex.title,
      handleNorm,
      ex.hero_accent,
      ex.hero_subheading,
      ex.location,
      ex.city,
      ex.country,
      ex.date_display,
      ex.exhibition_card_summary_text,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  function extendedHaystack(ex) {
    return [primaryHaystack(ex), ex.about_col_1_text, ex.artist_bio_excerpt]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  function haystackMatches(query, haystack) {
    if (haystack.indexOf(query) !== -1) return true;

    var words = query.split(/\s+/).filter(function (w) {
      return w && STOP_WORDS.indexOf(w) === -1;
    });
    if (!words.length) return false;

    return words.every(function (w) {
      return haystack.indexOf(w) !== -1;
    });
  }

  function matchesQuery(query, ex) {
    query = (query || '').toLowerCase().trim();
    if (!query || query.length < MIN_QUERY_LENGTH) return false;

    var handleNorm = (ex.handle || '').toLowerCase().replace(/-/g, ' ');
    if (handleNorm === query) return true;

    if (haystackMatches(query, primaryHaystack(ex))) return true;
    return haystackMatches(query, extendedHaystack(ex));
  }

  function searchExhibitions(query, max) {
    query = (query || '').trim();
    if (!query || query.length < MIN_QUERY_LENGTH) return Promise.resolve([]);

    return loadExhibitions().then(function (list) {
      var out = [];
      var i;
      for (i = 0; i < list.length; i += 1) {
        if (out.length >= max) break;
        var ex = list[i];
        if (!ex.url) continue;
        if (matchesQuery(query, ex)) out.push(ex);
      }
      return out;
    });
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function normalizeSearchPath(url) {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/+$/, '').toLowerCase();
    } catch (e) {
      return String(url || '')
        .split('?')[0]
        .replace(/\/+$/, '')
        .toLowerCase();
    }
  }

  function shopifyImageBase(url) {
    return String(url || '').split('?')[0];
  }

  function shopifyImageSrcset(url) {
    var base = shopifyImageBase(url);
    if (!base) return '';
    return [300, 450, 600]
      .map(function (width) {
        return base + '?width=' + width + ' ' + width + 'w';
      })
      .join(', ');
  }

  function removeExhibitionBlock(container) {
    if (!container) return;
    container.querySelectorAll('[data-exhibition-search-item]').forEach(function (el) {
      el.remove();
    });
  }

  function hideQuickSearchNoResults(container, hide) {
    var hdr = container.querySelector('.quick-search__no-results-header');
    var p = container.querySelector('.quick-search__no-results');
    if (hdr) hdr.hidden = hide;
    if (p) p.hidden = hide;
  }

  function hasNativeQuickSearchResults(container) {
    return !!container.querySelector(
      '.quick-search__result:not([data-exhibition-search-item]), .quick-search__suggested-queries-list-item'
    );
  }

  function renderPredictiveBlock(resultsContainer, query, matches, pauseObserver, isStale) {
    if (isStale()) return;

    if (!matches.length) {
      pauseObserver(function () {
        if (isStale()) return;
        removeExhibitionBlock(resultsContainer);
      });
      if (!hasNativeQuickSearchResults(resultsContainer)) hideQuickSearchNoResults(resultsContainer, false);
      return;
    }

    hideQuickSearchNoResults(resultsContainer, true);

    var renderKey = query + '::' + matches.map(function (ex) { return ex.url; }).join('|');
    var marker = resultsContainer.querySelector('[data-exhibition-search-header]');
    if (marker && marker.getAttribute('data-rendered') === renderKey) return;

    pauseObserver(function () {
      if (isStale()) return;

      removeExhibitionBlock(resultsContainer);

      var anchor = resultsContainer.firstChild;

      var heading = document.createElement('h4');
      heading.className = 'quick-search__header ff-body fs-body-100';
      heading.setAttribute('data-exhibition-search-item', '');
      heading.setAttribute('data-exhibition-search-header', '');
      heading.setAttribute('data-rendered', renderKey);
      heading.innerHTML =
        '<span class="t-subdued">' + escapeHtml(config.heading) + '</span>';
      resultsContainer.insertBefore(heading, anchor);

      matches.forEach(function (ex) {
        var link = document.createElement('a');
        link.className = 'quick-search__result';
        link.href = ex.url;
        link.setAttribute('data-exhibition-search-item', '');

        var title = document.createElement('div');
        title.className = 'quick-search__result-heading ff-body fs-body-75';
        title.textContent = ex.title || ex.handle || '';

        link.appendChild(title);
        resultsContainer.insertBefore(link, anchor);
      });
    });
  }

  function removeExhibitionGridItems(grid) {
    if (!grid) return;
    grid.querySelectorAll('[data-exhibition-search-item]').forEach(function (el) {
      el.remove();
    });
  }

  function createSearchItemImage(url, alt) {
    var imageWrap = document.createElement('div');
    imageWrap.className = 'search-item__image-wrapper';

    var imageDiv = document.createElement('div');
    imageDiv.className =
      'image search-item__image aspect-ratio--landscape_alt animation--lazy-load';

    var img = document.createElement('img');
    img.className = 'image__img';
    img.loading = 'lazy';
    img.src = shopifyImageBase(url) + '?width=600';
    img.srcset = shopifyImageSrcset(url);
    img.alt = alt || '';
    img.sizes =
      '(max-width: 720px) calc((90vw - 12px) /2), (max-width: 1400px) calc((93.4vw - (12px * 3)) / 4), 304px';
    img.onload = function () {
      imageDiv.classList.add('loaded');
    };

    imageDiv.appendChild(img);
    imageWrap.appendChild(imageDiv);

    if (img.complete) imageDiv.classList.add('loaded');

    return imageWrap;
  }

  function createSearchItemLink(ex) {
    var link = document.createElement('a');
    link.href = ex.url;
    link.setAttribute('data-exhibition-search-item', '');
    link.className =
      'search-item search-item--exhibition search-item--has-image animation--item animation--item-initial';

    if (ex.hero_image_url) {
      link.appendChild(
        createSearchItemImage(ex.hero_image_url, ex.hero_image_alt || ex.title || '')
      );
    } else {
      link.classList.remove('search-item--has-image');
    }

    var meta = document.createElement('div');
    meta.className = 'search-item__meta';

    var title = document.createElement('h4');
    title.className = 'search-item__title ff-heading fs-heading-5-base fs-heading-4-base-ns';
    title.textContent = ex.title || '';

    var readNow = document.createElement('span');
    readNow.className = 'search-item__link btn btn--callout';
    readNow.innerHTML = '<span>' + escapeHtml(config.readNow) + '</span>';

    meta.appendChild(title);
    meta.appendChild(readNow);
    link.appendChild(meta);

    var type = document.createElement('div');
    type.className = 'search-item__type fs-body-75';
    type.textContent = TYPE_LABEL;
    link.appendChild(type);

    return link;
  }

  function renderGridItems(grid, matches) {
    removeExhibitionGridItems(grid);
    if (!matches.length) return;

    var anchor = grid.firstChild;
    matches
      .slice()
      .reverse()
      .forEach(function (ex) {
        grid.insertBefore(createSearchItemLink(ex), anchor);
      });

    grid.classList.remove('search__results--no-products');

    var empty = grid.querySelector('.search__empty');
    if (empty) empty.remove();

    dedupeLegacyPageResults(grid, matches);
  }

  function dedupeLegacyPageResults(grid, matches) {
    var paths = {};
    matches.forEach(function (ex) {
      paths[normalizeSearchPath(ex.url)] = true;
    });

    grid.querySelectorAll('.search-item--page').forEach(function (el) {
      if (paths[normalizeSearchPath(el.href)]) el.remove();
    });
  }

  function initQuickSearch() {
    var root = document.querySelector('[data-quick-search]');
    if (!root) return;

    var results = root.querySelector('[data-results]');
    var input = root.querySelector('[data-input]');
    if (!results || !input) return;

    var mergeTimer;
    var mergeRequestId = 0;
    var observer = new MutationObserver(scheduleMerge);

    function pauseObserver(fn) {
      observer.disconnect();
      try {
        fn();
      } finally {
        observer.observe(results, { childList: true });
      }
    }

    function scheduleMerge() {
      clearTimeout(mergeTimer);
      mergeTimer = setTimeout(function () {
        var query = input.value.trim();

        if (!query || query.length < MIN_QUERY_LENGTH) {
          pauseObserver(function () {
            removeExhibitionBlock(results);
          });
          return;
        }

        var requestId = ++mergeRequestId;
        function isStale() {
          return requestId !== mergeRequestId || input.value.trim() !== query;
        }

        searchExhibitions(query, config.predictiveMax || 6).then(function (matches) {
          if (isStale()) return;
          renderPredictiveBlock(results, query, matches, pauseObserver, isStale);
        });
      }, 120);
    }

    observer.observe(results, { childList: true });
    input.addEventListener('input', scheduleMerge);
  }

  function initSearchPage() {
    var section = document.querySelector('[data-section-type="search"]');
    if (!section) return;

    var input = section.querySelector('[data-search-input]');
    var grid = section.querySelector('.search__results');
    if (!grid || !input) return;

    var query = input.value.trim();
    if (!query || query.length < MIN_QUERY_LENGTH) return;

    searchExhibitions(query, config.searchMax || 250).then(function (matches) {
      renderGridItems(grid, matches);
    });
  }

  function init() {
    if (hasQuickSearch) initQuickSearch();
    if (hasSearchPage) initSearchPage();
    if (hasQuickSearch || hasSearchPage) loadExhibitions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
